import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { runnerApi, errorText, type CellState, type GridMigration, type StatusGrid } from '../../../api/migrationRunner';
import { Alert, Btn, CELL_LABEL, ErrorBox, Kpi, Panel, Spinner, StateCell, bytes, shortName, when } from './ui';
import type { RunnerNav } from './nav';

const PROBLEM: CellState[] = ['failed', 'partial', 'interrupted'];
const DONE: CellState[] = ['applied', 'resolved', 'no_db'];

/**
 * Every company × every migration (approved mockup, "Status grid"): seven
 * states, click a cell for its run or a column for the preview, alerts and
 * KPIs on top, "Apply pending" for every not-run cell.
 */
const StatusTab: React.FC<{ nav: RunnerNav; isOwner: boolean; refreshKey: number }> = ({ nav, isOwner, refreshKey }) => {
  const [grid, setGrid] = useState<StatusGrid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setGrid(await runnerApi.status());
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load the status grid'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const stateOf = useCallback((m: GridMigration, db: string): CellState | null => grid?.cells[m.scriptId]?.[db]?.state ?? null, [grid]);

  const columns = useMemo(() => {
    if (!grid) return [];
    if (showAll) return grid.migrations;
    // Default: only migrations that still need something somewhere.
    return grid.migrations.filter((m) =>
      grid.targets.some((t) => {
        const st = stateOf(m, t.targetDb);
        return st !== null && !DONE.includes(st);
      }));
  }, [grid, showAll, stateOf]);

  const rows = useMemo(() => {
    if (!grid) return [];
    if (!onlyProblems) return grid.targets;
    return grid.targets.filter((t) => columns.some((m) => {
      const st = stateOf(m, t.targetDb);
      return st !== null && PROBLEM.includes(st);
    }));
  }, [grid, onlyProblems, columns, stateOf]);

  // Every not-run cell of a database that exists → one "Apply pending" batch.
  const pending = useMemo(() => {
    const pairs: string[] = [];
    const migs = new Set<number>();
    const dbs = new Set<string>();
    if (!grid) return { pairs, migs: [...migs], dbs: [...dbs] };
    for (const m of grid.migrations) {
      for (const t of grid.targets) {
        if (stateOf(m, t.targetDb) === 'not_run') {
          pairs.push(`${m.scriptId}|${t.targetDb}`);
          migs.add(m.scriptId);
          dbs.add(t.targetDb);
        }
      }
    }
    return { pairs, migs: [...migs], dbs: [...dbs] };
  }, [grid, stateOf]);

  const bootstrap = async () => {
    setBootstrapping(true);
    setBootstrapResult(null);
    try {
      const r = await runnerApi.bootstrap();
      const bad = r.results.find((x) => x.status !== 'success' && x.status !== 'skipped');
      setBootstrapResult(bad ? `${shortName(bad.migration)}: ${bad.error || bad.status}` : null);
      await load();
    } catch (err) {
      setBootstrapResult(errorText(err, 'Setup failed'));
    } finally {
      setBootstrapping(false);
    }
  };

  const onCell = (m: GridMigration, db: string) => {
    const cell = grid?.cells[m.scriptId]?.[db];
    if (!cell) return undefined;
    if (cell.runRowId && ['failed', 'partial', 'interrupted', 'resolved', 'running', 'applied'].includes(cell.state)) {
      return () => nav.openRun(cell.runRowId!);
    }
    if (cell.state === 'not_run') return () => nav.openPreview(m.scriptId);
    return undefined;
  };

  const cellTitle = (m: GridMigration, db: string, name: string) => {
    const c = grid?.cells[m.scriptId]?.[db];
    if (!c) return 'Not this database\'s scope';
    const bits = [`${name} · ${shortName(m.folderPath)}`, CELL_LABEL[c.state]];
    if (c.failedIndex !== undefined && c.failedIndex !== null) bits.push(`stopped at statement ${c.failedIndex + 1}`);
    if (c.errorReason) bits.push(c.errorReason);
    if (c.errorKind === 'already_applied') bits.push('the error says it is already there (not counted as a failure)');
    if (c.resolvedNote) bits.push(`"${c.resolvedNote}" (${c.resolvedBy})`);
    if (c.lastRun) bits.push(when(c.lastRun));
    return bits.join('\n');
  };

  if (!grid) {
    return (
      <div className="px-6 py-6">
        {error ? <ErrorBox>{error}</ErrorBox> : <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Loading every company × every migration…</div>}
      </div>
    );
  }

  const k = grid.kpis;
  const alerts = grid.alerts;
  const realFailures = k.failed + k.partial;

  return (
    <div className="space-y-4 px-6 py-6">
      {!grid.runnerReady && (
        <Alert
          tone="info"
          title="Set up the new runner"
          action={isOwner && <Btn onClick={bootstrap} disabled={bootstrapping}>{bootstrapping ? 'Applying…' : 'Apply runner tables to the registry'}</Btn>}
        >
          Batches, per-statement history, resume and resolve need the registry migration
          <code className="mx-1 rounded bg-white px-1">migrations-2026-10-11-migration-runner</code>
          (4 small files, safe to repeat). Until then runs are recorded the old way.
          {bootstrapResult && <div className="mt-1 text-red-700">{bootstrapResult}</div>}
        </Alert>
      )}

      {alerts.slice(0, 3).map((a) => (
        <Alert
          key={`${a.scriptId}|${a.targetDb}`}
          tone={a.state === 'interrupted' ? 'warn' : 'bad'}
          title={
            a.state === 'partial' ? `Half-applied on ${a.companyName}`
              : a.state === 'interrupted' ? `Interrupted on ${a.companyName}`
                : `Failed on ${a.companyName}`
          }
          action={a.runRowId ? <Btn kind={a.state === 'interrupted' ? 'ghost' : 'danger'} onClick={() => nav.openRun(a.runRowId!)}>{a.state === 'interrupted' ? 'Check and resume' : 'Fix it'}</Btn> : undefined}
        >
          {shortName(a.migration)}
          {a.state === 'partial' && a.failedIndex !== null && ` stopped at statement ${a.failedIndex + 1}. Statements 1–${a.failedIndex} are in the database; the rest are not.`}
          {a.state === 'interrupted' && ' was running when the server restarted. It is marked interrupted, not "new", so it won\'t silently run again.'}
          {a.state === 'failed' && (a.errorKind === 'already_applied' ? ` — ${a.reason} (looks already applied; resolve it if so).` : a.reason ? ` — ${a.reason}` : '')}
        </Alert>
      ))}
      {alerts.length > 3 && (
        <div className="text-[13px] text-slate-500">{alerts.length - 3} more need attention — use "Only problems" below.</div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="Companies" value={k.companies} foot={k.noDatabase ? `${k.noDatabase} without a database` : undefined} />
        <Kpi label="Pending migrations" value={k.pendingMigrations} foot={`${k.pendingCells} not-run cells`} />
        <Kpi label="Failed / partial" value={realFailures} tone={realFailures ? 'bad' : 'plain'} />
        <Kpi label="Interrupted" value={k.interrupted} tone={k.interrupted ? 'warn' : 'plain'} />
        <Kpi label="Folder warnings" value={k.folderErrors + k.folderWarnings} tone={k.folderErrors ? 'warn' : 'plain'} foot={k.folderErrors ? `${k.folderErrors} to fix` : undefined} />
      </div>

      <Panel
        title="Every company × every migration"
        actions={
          <>
            <Btn kind="ghost" small onClick={() => setOnlyProblems((v) => !v)} aria-pressed={onlyProblems}>
              {onlyProblems ? 'All companies' : 'Only problems'}
            </Btn>
            <Btn kind="ghost" small onClick={() => setShowAll((v) => !v)} aria-pressed={showAll}>
              {showAll ? 'Only migrations still to do' : `All ${grid.migrations.length} migrations`}
            </Btn>
            <Btn kind="ghost" small onClick={load} disabled={loading} aria-label="Refresh">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Btn>
            {isOwner && (
              <Btn small disabled={!grid.runnerReady || pending.pairs.length === 0} title={!grid.runnerReady ? 'Set up the runner first' : undefined}
                onClick={() => nav.openBatchDialog({
                  migrationIds: pending.migs, targets: pending.dbs, onlyPairs: pending.pairs,
                  title: `Apply pending (${pending.migs.length} migration${pending.migs.length === 1 ? '' : 's'} × ${pending.dbs.length} database${pending.dbs.length === 1 ? '' : 's'})`,
                })}>
                Apply pending ({pending.migs.length} × {pending.dbs.length})
              </Btn>
            )}
          </>
        }
        bodyClass="p-0"
      >
        {columns.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            Everything is applied everywhere. {grid.migrations.length > 0 && <button type="button" className="font-semibold text-blue-600 hover:underline" onClick={() => setShowAll(true)}>Show all migrations</button>}
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-auto">
            <table className="border-separate border-spacing-0 text-[13px]">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-20 min-w-[220px] border-b border-slate-200 bg-white px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Company</th>
                  {columns.map((m) => (
                    <th key={m.scriptId} className="sticky top-0 z-10 h-36 border-b border-slate-200 bg-white px-1 align-bottom">
                      <button
                        type="button"
                        onClick={() => nav.openPreview(m.scriptId)}
                        title={`${m.folderPath} (${m.scope}) — preview`}
                        className="mx-auto block max-h-32 overflow-hidden whitespace-nowrap text-left text-[11.5px] font-semibold text-slate-600 [writing-mode:vertical-rl] rotate-180 hover:text-blue-600"
                      >
                        {shortName(m.folderPath)}{m.scope === 'registry' ? ' (registry)' : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.targetDb} className="hover:bg-slate-50/70">
                    <td className="sticky left-0 z-10 border-b border-slate-100 bg-white px-3 py-1.5">
                      <b className="text-slate-900">{t.name}</b>
                      <div className="text-[11px] text-slate-400">
                        {t.scope === 'registry' ? (t.databaseName || 'registry') : t.targetDb}
                        {t.exists === false ? ' · no database' : t.sizeBytes ? ` · ${bytes(t.sizeBytes)}` : ''}
                      </div>
                    </td>
                    {columns.map((m) => {
                      const st = stateOf(m, t.targetDb);
                      return (
                        <td key={m.scriptId} className="border-b border-slate-100 px-1 py-1.5 text-center">
                          {st ? <StateCell state={st} title={cellTitle(m, t.targetDb, t.name)} onClick={onCell(m, t.targetDb)} /> : <span className="text-slate-200">·</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
          {(['applied', 'partial', 'failed', 'interrupted', 'resolved', 'not_run', 'no_db'] as CellState[]).map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5"><StateCell state={s} title={CELL_LABEL[s]} />{CELL_LABEL[s]}</span>
          ))}
          <span className="ml-auto">Click a cell for its run, a column for its preview.</span>
        </div>
      </Panel>
      {error && <ErrorBox>{error}</ErrorBox>}
    </div>
  );
};

export default StatusTab;
